import {
  API_ACCESS_KEY,
  API_REFRESH_KEY,
  API_URL,
  DATETIME_REGEX
} from 'constants/common'
import { NIVOS_LANGUAGES } from 'constants/layout'
import { LIGHT_MODE } from 'constants/theme'
import { Dispatch, bindActionCreators } from 'redux'
import { NivosLanguage } from 'types/layout'
import { NivosTheme } from 'types/theme'

// API FUNCTIONS

const clearSessionTokens = (): void => {
  // Will be used on user-logout
  localStorage.removeItem(API_ACCESS_KEY)
  localStorage.removeItem(API_REFRESH_KEY)
  window.location.href = '/'
}

const tryRefreshKey = async (
  fn: CallableFunction,
  kwargs: any[] = []
): Promise<any> => {
  const refreshKey = localStorage.getItem(API_REFRESH_KEY)
  if (refreshKey === null) return []
  return await post(
    '/account/token/refresh/',
    { refresh: refreshKey },
    false,
    true
  ).then(data => {
    localStorage.setItem(API_ACCESS_KEY, data.access)
    return fn(...kwargs)
  })
}

const get = async (
  endpoint: string,
  retried = false,
  returnRaw = false
): Promise<any> => {
  const defaultInit: RequestInit = {}
  const accessKey = localStorage.getItem(API_ACCESS_KEY)
  if (accessKey !== null) {
    defaultInit.headers = {
      Authorization: `Bearer ${accessKey}`
    }
  }
  return await fetch(API_URL + endpoint, { ...defaultInit }).then(
    async data => {
      if (data.status === 200) {
        if (returnRaw) return data
        const jsonData = await data.json()
        return camelizeFieldNames(jsonData)
      } else if (data.status === 401 && accessKey !== null && !retried) {
        return await tryRefreshKey(get, [endpoint, true])
      } else if (data.status === 401 && retried) {
        clearSessionTokens()
      }
      return await data.json()
    }
  )
}

// TODO: (BUG) POST on retry do not pass 'data' argument
const post = async (
  endpoint: string,
  data: any,
  isFormData = false,
  retried = false,
  method = 'POST'
): Promise<any> => {
  const defaultHeaders: any = {
    'Content-Type': 'application/json'
  }

  const accessKey = localStorage.getItem(API_ACCESS_KEY)

  if (accessKey !== null) {
    defaultHeaders.Authorization = `Bearer ${accessKey}`
  }

  if (isFormData) {
    delete defaultHeaders['Content-Type']
  }

  return await fetch(API_URL + endpoint, {
    method,
    headers: {
      ...defaultHeaders
    },
    body: isFormData ? data : JSON.stringify(data)
  }).then(async data => {
    if (data.status === 200 || data.status === 201) {
      const jsonData = await data.json()
      return camelizeFieldNames(jsonData)
    } else if (data.status === 401 && accessKey !== null && !retried) {
      return await tryRefreshKey(post, [endpoint, data, isFormData, true])
    } else if (data.status === 401 && retried) {
      clearSessionTokens()
    }
    try {
      return await data.json()
    } catch (e) {
      return data
    }
  })
}

const camelizeString = (str: string, separator = '_'): string => {
  // Transforms string format conventions
  // SAMPLE: from 'created_by' to 'createdBy'

  let newStr = str

  if (str.includes(separator)) {
    const parts = str.split(separator)
    newStr = parts[0]
    parts.shift()
    parts.forEach(part => {
      newStr += part.charAt(0).toUpperCase() + part.slice(1)
    })
  }

  return newStr
}

const camelizeFieldNames = (obj: Object): Object => {
  // Transforms naming convention
  // Problem: Django and Typescript has different naming convention
  //        : Django     = Snake Case (created_by)
  //        : Typescript = Camel Case (createdBy)

  // Solution: This function transforms all snake case fieldName into camel case

  if (obj === null || obj === undefined) {
    return obj
  }

  if (!Array.isArray(obj)) {
    const returnObj: any = {}

    Object.entries(obj).forEach(entry => {
      let fieldName = entry[0]
      let value = entry[1]
      if (fieldName.includes('_')) {
        fieldName = camelizeString(fieldName)
      }

      if (Array.isArray(value)) {
        // Recurse through child objects
        const temp = []
        for (let j = 0; j < value.length; j++) {
          if (typeof value[j] === 'object') {
            temp.push(camelizeFieldNames(value[j]))
          } else temp.push(value[j])
        }
        value = temp
      }
      if (typeof value === 'object') {
        value = camelizeFieldNames(value)
      }
      returnObj[fieldName] = value
    })
    return returnObj
  }

  const temp = []

  for (let j = 0; j < obj.length; j++) {
    if (typeof obj[j] === 'object') {
      temp.push(camelizeFieldNames(obj[j]))
    } else temp.push(obj[j])
  }

  return temp
}

const bind = (operation: any, dispatch: Dispatch<any>): any => {
  return bindActionCreators(operation, dispatch)
}

const mapOperationsToDispatchProps = (...operations: any[]) => {
  return (dispatch: Dispatch<any>): any => {
    return {
      operations: operations.reduce((previous, current) => {
        Object.keys(current).forEach(operation => {
          previous[operation] = bind(current[operation], dispatch)
        })
        return previous
      }, {})
    }
  }
}

const isNumber = (str: any): boolean => {
  if (typeof str !== 'string') return false // we only process strings!
  return !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}

const listNavigationControl = (
  stepDirection: 'left' | 'right',
  list: any[],
  currentSelectedIdx: number
): any => {
  const listLength = list.length - 1

  if (currentSelectedIdx < 0) return null

  if (stepDirection === 'right' && currentSelectedIdx === listLength) {
    return list[0]
  }

  if (stepDirection === 'left' && currentSelectedIdx === 0) {
    return list[listLength]
  }

  return stepDirection === 'right'
    ? list[currentSelectedIdx + 1]
    : list[currentSelectedIdx - 1]
}

const srcset = (
  image: string,
  size: number,
  rows = 1,
  cols = 1
): { src: string, srcSet: string } => {
  return {
    src: `${image}&w=${size * cols}&h=${size * rows}&fit=crop&auto=format`,
    srcSet: `${image}&w=${size * cols}&h=${
      size * rows
    }&fit=crop&auto=format&dpr=2 2x`
  }
}

const capitalize = (str: string): string => {
  return str
    .replace(/^\w|[A-Z]|\b\w/g, word => {
      return word.toUpperCase()
    })
    .replace(/\s+/g, ' ')
}

const camelToSnakeCase = (str: string): string =>
  str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

const isDateString = (str: string): boolean => DATETIME_REGEX.test(str)

const isString = (obj: any): boolean =>
  typeof obj === 'string' || obj instanceof String

const objectifyDateStringFields = (obj: Object): any => {
  if (obj === null || obj === undefined) {
    return obj
  }
  const returnObj: any = {}
  const entries = Object.entries(obj)

  for (let i = 0; i < entries.length; i++) {
    returnObj[entries[i][0]] = entries[i][1]
    if (isString(entries[i][1]) && isDateString(entries[i][1])) {
      // Evil bug! the condition above needs to be repeated in order to work
      isString(entries[i][1]) && isDateString(entries[i][1])
      returnObj[entries[i][0]] = new Date(entries[i][1])
    }
    if (Array.isArray(entries[i][1])) {
      // Recurse through child objects
      const temp = []
      for (let j = 0; j < entries[i][1].length; j++) {
        if (typeof entries[i][1][j] === 'object') {
          temp.push(objectifyDateStringFields(entries[i][1][j]))
        } else temp.push(entries[i][1][j])
      }
      returnObj[entries[i][0]] = temp
    }
  }

  return returnObj
}

const datesAreOnSameDay = (first: Date, second: Date): boolean =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate()

const getSavedTheme = (): NivosTheme => {
  const savedTheme: string | null = localStorage.getItem('nivos_theme')

  if (savedTheme === null) return LIGHT_MODE

  return JSON.parse(savedTheme) as NivosTheme
}

const getSavedLanguage = (): NivosLanguage => {
  const savedLanguage: string | null = localStorage.getItem('nivos_language')

  if (savedLanguage === null) return NIVOS_LANGUAGES.eng

  return JSON.parse(savedLanguage) as NivosLanguage
}

const trusty = (val: any): boolean => {
  const faulty = [undefined, null, false, '', 0]

  return !faulty.includes(val)
}

export {
  camelizeFieldNames,
  camelizeString,
  clearSessionTokens,
  mapOperationsToDispatchProps,
  isNumber,
  listNavigationControl,
  srcset,
  capitalize,
  get,
  post,
  objectifyDateStringFields,
  isDateString,
  isString,
  datesAreOnSameDay,
  getSavedTheme,
  getSavedLanguage,
  camelToSnakeCase,
  trusty
}
